import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { IsNewDirective } from '../../../../directives/is-new.directive';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { SortComponent } from '../../../core/components/sort/sort.component';
import { DatepickerComponent } from '../../../core/components/datepicker/datepicker.component';
import { DateRangeInput } from '../../../core/models/date-range-input';
import { processDateRange } from '../../../../helper/date-utils';
import { getAlertColor } from '../../../events-alarms/alert-colors';
import { Client, EventDataOut, EventQueryDataIn, IndividualAlarmPageDataOut, IndividualAlarmPageQueryDataIn, PageInfo, SortingType } from '../../../../api/api-reference';

@Component({
  selector: 'app-events-alarms',
  standalone: true,
  imports: [CommonModule, TranslateModule, IsNewDirective, PaginationComponent, SortComponent, DatepickerComponent],
  templateUrl: './events-alarms.component.html',
  styleUrl: './events-alarms.component.scss',
})
export class EventsAlarmsComponent implements OnInit {
  @Input() meterNo = '';

  subTabs = [
    { id: 'Events', labelKey: 'EVENTS' },
    { id: 'Alarms', labelKey: 'ALARMS' },
  ];
  activeSubTab = this.subTabs[0].id;
  getAlertColor = getAlertColor;

  events: EventDataOut[] = [];
  numberOfEvents = 0;
  eventsLastPageSize = 1;

  // NOTE: EventQueryDataIn has no meter-id/meter-number filter field — the query endpoint only
  // supports a fuzzy `search` (LIKE match against meter number OR asset number), so this tab can
  // in rare cases show events for another meter/asset whose number contains this meter's number
  // as a substring. There's no exact-match option available from the current API.
  eventsRequestData = new EventQueryDataIn({
    pageInfo: new PageInfo({ pageSize: 10, page: 1 }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    assetTypes: [],
    channelIds: [],
  });

  alarms: IndividualAlarmPageDataOut[] = [];
  numberOfAlarms = 0;
  alarmsLastPageSize = 1;

  // same fuzzy-search caveat as eventsRequestData above — IndividualAlarmPageQueryDataIn has no
  // meter-id/meter-number filter field either, only a Search substring match on Meter.MeterNumber
  alarmsRequestData = new IndividualAlarmPageQueryDataIn({
    pageInfo: new PageInfo({ pageSize: 10, page: 1 }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    assetTypes: [],
    alarmTypes: [],
  });

  eventsPageSize = 10;
  eventsCurrentPage = 1;
  alarmsPageSize = 10;
  alarmsCurrentPage = 1;

  isEventsDatepickerOpen = false;
  eventsStartDate?: Date;
  eventsEndDate?: Date;
  eventsSelectedDate: string | null = '';

  isAlarmsDatepickerOpen = false;
  alarmsStartDate?: Date;
  alarmsEndDate?: Date;
  alarmsSelectedDate: string | null = '';

  constructor(private client: Client, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.loadEvents(1);
    this.loadAlarms(1);
  }

  loadEvents(page: number = 1): void {
    this.eventsCurrentPage = page;
    this.eventsRequestData.pageInfo!.page = page;
    this.eventsRequestData.pageInfo!.pageSize = this.eventsPageSize;
    this.eventsRequestData.search = this.meterNo || undefined;
    this.client.query2(this.eventsRequestData).subscribe({
      next: (response) => {
        this.events = response.data ?? [];
        this.numberOfEvents = response.count || 0;
        this.eventsLastPageSize = Math.ceil(this.numberOfEvents / this.eventsPageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  eventsPageChange(page: number) {
    this.loadEvents(page);
  }

  toggleEventsDatepicker() {
    this.isEventsDatepickerOpen = !this.isEventsDatepickerOpen;
  }

  onEventsSortOption(option: string) {
    this.eventsRequestData.sorting = option === 'ASCENDING' ? SortingType.Ascending : SortingType.Descending;
    this.loadEvents(1);
  }

  onEventsDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);
    this.eventsStartDate = result.startDate;
    this.eventsEndDate = result.endDate;
    this.eventsSelectedDate = result.selectedDate;
    this.eventsRequestData.startDate = result.startDate;
    this.eventsRequestData.endDate = result.endDate;
    this.loadEvents(1);
  }

  clearEventsDates(): void {
    this.eventsStartDate = undefined;
    this.eventsEndDate = undefined;
    this.eventsSelectedDate = '';
    this.eventsRequestData.startDate = undefined;
    this.eventsRequestData.endDate = undefined;
    this.loadEvents(1);
  }

  loadAlarms(page: number = 1): void {
    this.alarmsCurrentPage = page;
    this.alarmsRequestData.pageInfo!.page = page;
    this.alarmsRequestData.pageInfo!.pageSize = this.alarmsPageSize;
    this.alarmsRequestData.search = this.meterNo || undefined;
    this.client.getPage(this.alarmsRequestData).subscribe({
      next: (response) => {
        this.alarms = response.data ?? [];
        this.numberOfAlarms = response.count || 0;
        this.alarmsLastPageSize = Math.ceil(this.numberOfAlarms / this.alarmsPageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  alarmsPageChange(page: number) {
    this.loadAlarms(page);
  }

  toggleAlarmsDatepicker() {
    this.isAlarmsDatepickerOpen = !this.isAlarmsDatepickerOpen;
  }

  onAlarmsDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);
    this.alarmsStartDate = result.startDate;
    this.alarmsEndDate = result.endDate;
    this.alarmsSelectedDate = result.selectedDate;
    this.alarmsRequestData.startDate = result.startDate;
    this.alarmsRequestData.endDate = result.endDate;
    this.loadAlarms(1);
  }

  clearAlarmsDates(): void {
    this.alarmsStartDate = undefined;
    this.alarmsEndDate = undefined;
    this.alarmsSelectedDate = '';
    this.alarmsRequestData.startDate = undefined;
    this.alarmsRequestData.endDate = undefined;
    this.loadAlarms(1);
  }

  getDuration(startTime?: Date, endTime?: Date): string {
    if (!startTime || !endTime) return '/';
    const diffMs = endTime.getTime() - startTime.getTime();
    if (diffMs <= 0) return '/';
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  selectSubTab(tab: string) {
    this.activeSubTab = tab;
  }

  onAlarmsSortOption(option: string) {
    this.alarmsRequestData.sorting = option === 'ASCENDING' ? SortingType.Ascending : SortingType.Descending;
    this.loadAlarms(1);
  }
}
